import { useState } from 'react';
import { fmt } from '@nyce/admin-kit';
import { Button } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useBuddyWrite } from '../../api/client';
import type { LimitScope, TokenLimit, TokenLimitInput } from '../../api/setup';
import { LimitMeter } from '../../shared';
import { UsageLink, messageOf, useQueryParam, useSetup } from './shared';

// The AUTH tenant is no budget since buddy3 0015 (one tenant can serve two organizations): a system rolls up to its
// organization, and the first limit reached (system → organization) blocks the chat.
const SCOPES: { value: LimitScope; label: string; column: string }[] = [
  { value: 'organization', label: 'Organizations', column: 'Organization' },
  { value: 'system', label: 'Systems', column: 'System' },
];

interface Draft {
  id: string;
  limit: string;
  warn: string;
}

/** The PUT body of a draft, or why it cannot be saved. Empty limit = unlimited, empty warn = buddy3's default (80). */
export function limitBody(d: Pick<Draft, 'limit' | 'warn'>): TokenLimitInput | string {
  const monthlyLimit = d.limit.trim() === '' ? null : Number(d.limit);
  const warnPercent = d.warn.trim() === '' ? null : Number(d.warn);
  if (monthlyLimit != null && (!Number.isInteger(monthlyLimit) || monthlyLimit < 0))
    return 'The monthly limit is a whole number of NYCE tokens (empty = unlimited).';
  if (warnPercent != null && (!Number.isFinite(warnPercent) || warnPercent < 1 || warnPercent > 100))
    return 'Warn % is between 1 and 100 (empty = default).';
  return { monthlyLimit, isUnlimited: monthlyLimit == null, warnPercent };
}

/** Monthly NYCE token budgets per organization and system, with this month's use. */
export function BuddyLimitsPage() {
  const { target, api } = useSetup();
  const canWrite = useBuddyWrite();
  const [scopeRaw, setScope] = useQueryParam('scope');
  const scope: LimitScope = scopeRaw === 'system' ? 'system' : 'organization';
  const limits = useAsync(() => api.limits.list(scope), [api, scope]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useNotice();
  const meta = SCOPES.find((s) => s.value === scope)!;
  // Legacy tenant rows (before 0015) are not budgets any more.
  const rows = (limits.data ?? []).filter((l) => l.scope !== 'tenant');

  const save = async () => {
    if (!editing) return;
    const body = limitBody(editing);
    if (typeof body === 'string') return setError(body);
    setBusy(true);
    try {
      await api.limits.put(scope, editing.id, body);
      toast.success(`Saved the limit of ${rows.find((r) => r.scopeId === editing.id)?.name ?? editing.id}`);
      setEditing(null);
      setError(null);
      await limits.reload();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const edit = (l: TokenLimit) => {
    setError(null);
    setEditing({
      id: l.scopeId,
      limit: l.monthlyLimit == null || l.isUnlimited ? '' : String(l.monthlyLimit),
      warn: l.warnPercent == null ? '' : String(l.warnPercent),
    });
  };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') void save();
    if (e.key === 'Escape') setEditing(null);
  };

  return (
    <Page
      title="Token limits"
      description="Monthly NYCE token budgets per organization and system. The first limit reached blocks the chat until the month resets."
    >
      <ErrorBox error={limits.error ?? error} />
      {toast.element}
      <div className="nyk-card">
        <div className="nyk-card-head">
          <Tabs
            value={scope}
            onChange={(v) => {
              setScope(v === 'organization' ? null : v);
              setEditing(null);
              setError(null);
            }}
            options={SCOPES}
          />
          <span className="nyk-ui-hint">
            NYCE tokens = real tokens × the model’s token factor. Empty limit = unlimited; warn % shows the
            chat’s banner before the limit (default 80).
          </span>
        </div>
        <Loading loading={limits.loading && !limits.data} />
        {limits.data && !rows.length && <EmptyState title={`No ${meta.label.toLowerCase()} yet.`} />}
        {!!rows.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="limits-table" data-scope={scope}>
              <thead>
                <tr>
                  <th>{meta.column}</th>
                  {scope === 'system' && <th>Organization</th>}
                  <th className="nyk-ui-num">Monthly limit</th>
                  <th className="nyk-ui-num">Warn %</th>
                  <th>Used this month</th>
                  <th>Updated</th>
                </tr>
              </thead>
              {rows.map((l) => {
                const isEditing = editing?.id === l.scopeId;
                return (
                  <tbody key={l.scopeId} className="nyk-row-group" data-limit={l.scopeId}>
                    <tr>
                      <td className="nyk-card-title">
                        <b>{l.name ?? l.scopeId}</b>
                        <div className="nyk-ui-hint">{l.scopeId}</div>
                      </td>
                      {scope === 'system' && (
                        <td data-label="Organization">
                          {l.organizationName ?? l.organizationId ?? <span className="nyk-ui-hint">—</span>}
                        </td>
                      )}
                      <td className="nyk-ui-num" data-label="Monthly limit">
                        {isEditing ? (
                          <input
                            className="nyk-ui-input bd-cell-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            aria-label={`Monthly limit of ${l.name ?? l.scopeId}`}
                            placeholder="unlimited"
                            value={editing.limit}
                            onChange={(e) => setEditing({ ...editing, limit: e.target.value })}
                            onKeyDown={keys}
                            autoFocus
                          />
                        ) : l.monthlyLimit && !l.isUnlimited ? (
                          fmt(l.monthlyLimit)
                        ) : (
                          <span className="nyk-ui-hint">{l.stored === false ? 'no limit' : 'unlimited'}</span>
                        )}
                      </td>
                      <td className="nyk-ui-num" data-label="Warn %">
                        {isEditing ? (
                          <input
                            className="nyk-ui-input bd-cell-input"
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={100}
                            aria-label={`Warn percent of ${l.name ?? l.scopeId}`}
                            placeholder="80"
                            value={editing.warn}
                            onChange={(e) => setEditing({ ...editing, warn: e.target.value })}
                            onKeyDown={keys}
                          />
                        ) : l.warnPercent != null ? (
                          `${l.warnPercent} %`
                        ) : (
                          <span className="nyk-ui-hint">default</span>
                        )}
                      </td>
                      <td data-label="Used this month">
                        <span className="nyk-ui-hint">{fmt(l.usedTokens ?? 0)}</span>{' '}
                        <LimitMeter
                          used={l.usedTokens}
                          limit={l.isUnlimited ? null : l.monthlyLimit}
                          unlimited={l.isUnlimited}
                        />
                      </td>
                      <td className="nyk-ui-hint" data-label="Updated">
                        {l.updatedBy ?? '—'}
                        {l.updatedAt && <div>{new Date(l.updatedAt).toLocaleDateString()}</div>}
                      </td>
                    </tr>
                    <ActionsRow colSpan={scope === 'system' ? 6 : 5}>
                      <UsageLink kind={scope} id={l.scopeId} target={target} />
                      {canWrite &&
                        (isEditing ? (
                          <>
                            <Button size="sm" onClick={() => void save()} loading={busy}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => edit(l)}>
                            Edit
                          </Button>
                        ))}
                    </ActionsRow>
                  </tbody>
                );
              })}
            </table>
          </TableScroller>
        )}
      </div>
    </Page>
  );
}
